import React from "react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import type { StackScreenProps } from "@react-navigation/stack";
import { SexAtBirthScreen } from "./SexAtBirthScreen/SexAtBirthScreen";
import { BasicInfoScreen } from "./BasicInfoScreen/BasicInfoScreen";
import { QuickExposureScreen } from "./QuickExposureScreen/QuickExposureScreen";
import { PregnancyScreen } from "./PregnancyScreen/PregnancyScreen";
import { EndometriosisScreen } from "./EndometriosisScreen/EndometriosisScreen";
import { PCOSScreen } from "./PCOSScreen/PCOSScreen";
import { VaricoceleScreen } from "./VaricoceleScreen/VaricoceleScreen";
import { CancerScreen } from "./CancerScreen/CancerScreen";
import { AllergiesScreen } from "./AllergiesScreen/AllergiesScreen";
import { AllergiesDetailsScreen } from "./AllergiesDetailsScreen/AllergiesDetailsScreen";
import { MedicationsScreen } from "./MedicationsScreen/MedicationsScreen";
import { AboutYouComplete } from "./AboutYouComplete/AboutYouComplete";
import { BasicInfoProvider } from "./BasicInfoContext";
import { BasicInfoStackParamList } from "./types";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { calculateBasicInfoCompletion } from "./calculateBasicInfoCompletion";
import { MedicalHistoryQuestionProvider } from "@src/packages/medical-history/components";

export type BasicInfoStackScreenProps<T extends keyof BasicInfoStackParamList> =
  StackScreenProps<BasicInfoStackParamList, T>;

const Stack = createNativeStackNavigator<BasicInfoStackParamList>();

/**
 * Map completion field names to the screen that collects them.
 */
const FIELD_TO_SCREEN: Record<string, keyof BasicInfoStackParamList> = {
  dateOfBirth: "BasicInfo",
  height: "BasicInfo",
  weight: "BasicInfo",
  quickExposure: "QuickExposure",
  hasBeenPregnant: "Pregnancy",
  pregnancyCount: "Pregnancy",
  hasEndometriosis: "Endometriosis",
  suspectPCOS: "PCOS",
  hadVaricoceles: "Varicocele",
  hasCancerHistory: "Cancer",
  hasAllergies: "Allergies",
  allergyDetails: "AllergiesDetails",
};

function getInitialRoute(
  patient: ReturnType<typeof usePatient>["data"],
): keyof BasicInfoStackParamList {
  if (!patient?.gender) return "SexAtBirth";

  const completion = calculateBasicInfoCompletion(patient);
  if (!completion.breakdown?.missing.length) return "AboutYouComplete";

  const firstMissing = completion.breakdown?.missing[0];
  return (firstMissing && FIELD_TO_SCREEN[firstMissing.name]) ?? "BasicInfo";
}

export function BasicInfoNavigator() {
  const { data: patient } = usePatient();

  return (
    <BasicInfoProvider>
      <MedicalHistoryQuestionProvider
        section="basic-info"
        sectionLabelKey="med_history.review.section_title.basic-info"
        helpTextKey="med_history.question_screen.flow_help.basic-info"
      >
        <Stack.Navigator
          initialRouteName={getInitialRoute(patient)}
          screenOptions={{
            headerShown: false,
            contentStyle: { backgroundColor: "transparent" },
          }}
        >
          <Stack.Screen name="SexAtBirth" component={SexAtBirthScreen} />
          <Stack.Screen name="BasicInfo" component={BasicInfoScreen} />
          <Stack.Screen name="QuickExposure" component={QuickExposureScreen} />
          <Stack.Screen name="Pregnancy" component={PregnancyScreen} />
          <Stack.Screen name="Endometriosis" component={EndometriosisScreen} />
          <Stack.Screen name="PCOS" component={PCOSScreen} />
          <Stack.Screen name="Varicocele" component={VaricoceleScreen} />
          <Stack.Screen name="Cancer" component={CancerScreen} />
          <Stack.Screen name="Allergies" component={AllergiesScreen} />
          <Stack.Screen
            name="AllergiesDetails"
            component={AllergiesDetailsScreen}
          />
          <Stack.Screen name="Medications" component={MedicationsScreen} />
          <Stack.Screen name="AboutYouComplete" component={AboutYouComplete} />
        </Stack.Navigator>
      </MedicalHistoryQuestionProvider>
    </BasicInfoProvider>
  );
}
